/**
 * SonmiSignonBonus — hidden admin route at `/admin/sonmi/sign-on-bonus`.
 *
 * The six-phase trust loop, rendered:
 *   · If ?employee=<id> is present and no permission exists yet → show
 *     SONMI's offer. Grant creates the permission + obligation.
 *   · Otherwise → list every active + revoked permission with its
 *     obligation basis, ledger, and confidence events.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import { sonmiCopy } from "@/lib/sonmiCopy";
import { OfferCard } from "@/components/sonmi/OfferCard";
import { PermissionCard } from "@/components/sonmi/PermissionCard";

export default function SonmiSignonBonus() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const employeeId = params.get("employee");

  const [offer, setOffer] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [detail, setDetail] = useState({});   // permission_id -> {permission, obligation, ledger}
  const [busy, setBusy] = useState(false);

  const loadOffer = useCallback(async () => {
    if (!employeeId) { setOffer(null); return; }
    try {
      const r = await api.get(`/sonmi/sign-on-bonus/offer/${employeeId}`);
      setOffer(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't reach SONMI.");
    }
  }, [employeeId]);

  const loadPermissions = useCallback(async () => {
    try {
      const r = await api.get(`/sonmi/permissions`);
      // Filter to sign-on bonus scopes only.
      const rows = (r.data?.permissions || []).filter(
        (p) => (p.scope || "").startsWith("sign_on_bonus.")
      );
      setPermissions(rows);
      // Fetch details in parallel.
      const detailMap = {};
      await Promise.all(rows.map(async (p) => {
        try {
          const d = await api.get(`/sonmi/permissions/${p.permission_id}`);
          detailMap[p.permission_id] = d.data;
          // Also fetch the employee record for display name.
          const empId = d.data?.obligation?.employee_id;
          if (empId) {
            try {
              const e = await api.get(`/sonmi/employees/${empId}`);
              detailMap[p.permission_id].employee = e.data.employee;
            } catch {}
          }
        } catch {}
      }));
      setDetail(detailMap);
    } catch (e) {
      toast.error("Couldn't load permissions.");
    }
  }, []);

  useEffect(() => { loadOffer(); loadPermissions(); }, [loadOffer, loadPermissions]);

  const onGrant = async () => {
    setBusy(true);
    try {
      await api.post(`/sonmi/sign-on-bonus/grant/${employeeId}`, {
        reason: "Admin accepted SONMI's offer",
      });
      toast.success(sonmiCopy.toast.granted);
      // Clear the ?employee param and refresh.
      setParams({});
      setOffer(null);
      await loadPermissions();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't grant.");
    } finally {
      setBusy(false);
    }
  };

  const onDecline = () => {
    setParams({});
    setOffer(null);
  };

  const onRevoke = async (permissionId, reason) => {
    try {
      await api.post(`/sonmi/permissions/${permissionId}/revoke`, { reason });
      toast.success(sonmiCopy.toast.revoked);
      await loadPermissions();
    } catch (e) {
      toast.error("Couldn't revoke.");
    }
  };

  const active = permissions.filter((p) => !p.revoked_at);
  const revoked = permissions.filter((p) => p.revoked_at);

  return (
    <div className="min-h-screen bg-stone-50">
      <Topbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold text-stone-900">
            {sonmiCopy.page.signon.title}
          </h1>
          <p className="mt-3 text-stone-600 max-w-2xl">
            {sonmiCopy.page.signon.lede}
          </p>
        </header>

        {/* Phase 2 · Offer */}
        {offer && offer.kind === "new_offer" && (
          <div className="mb-8">
            <OfferCard offer={offer} onGrant={onGrant} onDecline={onDecline} busy={busy} />
          </div>
        )}
        {offer && offer.kind === "already_carrying" && (
          <div className="mb-8 rounded-2xl border border-stone-200 bg-white px-6 py-5">
            <div className="text-sm text-stone-800" data-testid="already-carrying-notice">
              {offer.message}
            </div>
            <button
              type="button"
              onClick={onDecline}
              className="mt-3 text-sm text-stone-600 hover:text-stone-900"
            >
              {sonmiCopy.common.close}
            </button>
          </div>
        )}

        {/* Active permissions */}
        {active.length > 0 && (
          <section className="mb-8" data-testid="active-permissions-section">
            <h2 className="text-xs uppercase tracking-widest text-stone-500 mb-3">
              What SONMI is carrying for you
            </h2>
            <div className="space-y-3">
              {active.map((p) => {
                const d = detail[p.permission_id] || {};
                return (
                  <PermissionCard
                    key={p.permission_id}
                    permission={d.permission || p}
                    obligation={d.obligation}
                    ledger={d.ledger || []}
                    employee={d.employee}
                    onRevoke={onRevoke}
                  />
                );
              })}
            </div>
          </section>
        )}

        {/* Revoked / historical */}
        {revoked.length > 0 && (
          <section className="mb-8" data-testid="revoked-permissions-section">
            <h2 className="text-xs uppercase tracking-widest text-stone-500 mb-3">
              No longer carrying
            </h2>
            <div className="space-y-3">
              {revoked.map((p) => {
                const d = detail[p.permission_id] || {};
                return (
                  <PermissionCard
                    key={p.permission_id}
                    permission={d.permission || p}
                    obligation={d.obligation}
                    ledger={d.ledger || []}
                    employee={d.employee}
                    onRevoke={() => {}}
                  />
                );
              })}
            </div>
          </section>
        )}

        {!offer && permissions.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-300 bg-white/70 px-6 py-10 text-center">
            <div className="text-sm text-stone-600 max-w-md mx-auto leading-relaxed">
              SONMI doesn't have anything to carry yet. Add an employee, and if
              they're new, she'll notice and offer to take their sign-on
              bonus off your plate.
            </div>
            <button
              type="button"
              onClick={() => navigate("/admin/employees")}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-stone-900 hover:bg-stone-800"
              data-testid="go-to-employees-btn"
            >
              Go to Employees
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
